import React, { useCallback, useEffect, useRef, useState } from "react";

import DevelopmentStage from "../../../components/custom_creative_session/DevelopmentStage";
import ReviewStage from "../../../components/custom_creative_session/ReviewStage";
import WorkspaceChat from "../../../components/custom_creative_session/WorkspaceChat";
import { useCcsApi } from "../../../components/custom_creative_session/useCcsApi";

/**
 * Workstation · Ads & Creative — **one open Custom session**, in v2.
 *
 * THE RULING THIS SCREEN IMPLEMENTS
 * =================================
 * Founder, 2026-09-08: *"When someone is in the middle of a new session, they
 * should not see previous sessions on that screen at all."*
 *
 * This component renders no list and imports nothing that does. The guarantee
 * is enforced one level up, in `AdsCreative.jsx`, which renders EITHER the
 * sessions screen OR this one on `?session=` — never both. A structural either/
 * or, not a hidden element, because a list that is merely not displayed is
 * still one CSS change away from being displayed.
 *
 * WHY THE STAGES ARE IMPORTED RATHER THAN REWRITTEN
 * =================================================
 * This is the reuse case `CREATIVE_V2_INVENTORY.md` called the strongest in the
 * codebase, and re-verified in `V2_CREATIVE_AUTHORING_SCOPE.md`: all thirteen
 * CCS calls live in one hook (`useCcsApi.js`), and the five components make
 * **zero** direct HTTP calls. The stage machine (DEVELOPMENT → REVIEW →
 * SUBMITTED), submitted-is-final and ownership are all enforced server-side and
 * independently of the UI. So the stages hold no rules to move: they are
 * presentation over a hook, and v2 mounts the same presentation over the same
 * hook rather than growing a second copy that can drift from it.
 *
 * WHAT IS HONESTLY NOT DONE HERE
 * ==============================
 * The stage panels still carry their v1 Tailwind styling. Tailwind's content
 * glob is `./src/**`, so they render correctly inside the v2 shell — but they
 * are not yet wearing the v2 design system. The navigation, the chrome and the
 * session model around them ARE v2. Restyling the panel interiors is follow-on
 * work and is stated rather than quietly claimed.
 */

const DEV_TARGETS = [
  { key: "hook", label: "Hook" },
  { key: "script_body", label: "Script Body" },
  { key: "cta", label: "CTA" },
];
const REVIEW_TARGETS = [
  { key: "primary_hook", label: "Primary Hook" },
  { key: "body", label: "Body" },
  { key: "primary_cta", label: "Primary CTA" },
];
const STAGE_LABEL = {
  DEVELOPMENT: "Development",
  REVIEW: "Review",
  SUBMITTED: "Submitted",
};
const STAGES = ["DEVELOPMENT", "REVIEW", "SUBMITTED"];

function SubmittedView({ session }) {
  const submission = session.submission || {};
  return (
    <div className="ws-ccs-submitted" data-testid="ws-ccs-submitted">
      <h3>Submitted to production</h3>
      <p className="dim">
        This session is finished and read-only. The server refuses writes to a
        submitted session, so nothing here can be changed by accident.
      </p>
      {submission.destination_url ? (
        <p>
          <a href={submission.destination_url} target="_blank" rel="noopener noreferrer">
            Open the production card
          </a>
        </p>
      ) : (
        <p className="dim">
          No destination link was recorded for this submission.
        </p>
      )}
    </div>
  );
}

export default function CustomSessionScreen({ sessionId, onClose }) {
  const api = useCcsApi();
  const [session, setSession] = useState(null);
  // loading | error | ready — three states, because "still loading" and
  // "failed to load" must not both render as an empty panel.
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);

  // The copy-to-field bridge: the active stage registers its handler, the chat
  // calls it. Same wiring as v1's container, for the same reason — the chat is
  // mounted once and must reach whichever stage is showing.
  const copyHandlerRef = useRef(null);
  const setCopyHandler = useCallback((fn) => {
    copyHandlerRef.current = fn;
  }, []);
  const handleCopyToField = useCallback((key, text) => {
    if (copyHandlerRef.current) copyHandlerRef.current(key, text);
  }, []);

  // `background` — a refresh of a session already on screen, not an open.
  //
  // BUG, found live 2026-09-08 and introduced by this lane: this always set
  // "loading", which swapped the WHOLE screen for the loading state and
  // unmounted the stage. `ReviewStage.openConfirm` refreshes before opening its
  // confirmation modal, so clicking "Submit to Production" destroyed the
  // component that was about to show the modal and remounted it with
  // `confirmOpen` back to false — the screen blinked and nothing happened.
  //
  // v1's container has always guarded this with `if (loading && !session)`. The
  // condition was dropped in the port. A background refresh now leaves what is
  // on screen alone, which is the whole point of a refresh.
  const load = useCallback(
    async (id, { background = false } = {}) => {
      if (!background) setState("loading");
      setError(null);
      try {
        setSession(await api.getSession(id));
        setState("ready");
      } catch (e) {
        // The CCS endpoints answer 404 for a session that is not yours as well
        // as one that does not exist — deliberately, so they never confirm
        // someone else's session id. Say both, rather than picking one.
        const status = e?.response?.status;
        setError(
          status === 404
            ? "That session could not be opened. It does not exist, or it belongs to another operator."
            : e?.response?.data?.detail || e?.message || "could not load the session",
        );
        setState("error");
      }
    },
    [api],
  );

  useEffect(() => {
    if (sessionId) load(sessionId);
  }, [sessionId, load]);

  const refreshSession = useCallback(async () => {
    if (sessionId) await load(sessionId, { background: true });
  }, [sessionId, load]);
  const applySession = useCallback((s) => {
    if (s) setSession(s);
  }, []);

  const back = (
    <button
      type="button"
      className="btn-ghost btn-sm"
      onClick={onClose}
      data-testid="ws-ccs-back"
    >
      ← All sessions
    </button>
  );

  // `&& !session` matters: without it a background refresh blanks a screen the
  // operator is working in. That was the bug.
  if (state === "loading" && !session) {
    return (
      <div className="ws-ccs" data-testid="ws-ccs-loading">
        {back}
        <div className="state ws-state">Opening the session…</div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="ws-ccs" data-testid="ws-ccs-error">
        {back}
        <div className="state ws-state state-error" role="alert">
          <p>{error}</p>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => load(sessionId)}
            data-testid="ws-ccs-retry"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const stage = session?.stage;
  const copyTargets = stage === "REVIEW" ? REVIEW_TARGETS : DEV_TARGETS;

  let left;
  if (stage === "DEVELOPMENT") {
    left = (
      <DevelopmentStage
        session={session}
        api={api}
        applySession={applySession}
        refreshSession={refreshSession}
        setCopyHandler={setCopyHandler}
      />
    );
  } else if (stage === "REVIEW") {
    left = (
      <ReviewStage
        session={session}
        api={api}
        applySession={applySession}
        refreshSession={refreshSession}
        setCopyHandler={setCopyHandler}
      />
    );
  } else {
    left = <SubmittedView session={session} />;
  }

  return (
    <div className="ws-ccs" data-testid="ws-ccs-screen">
      <div className="ws-ccs-head">
        {back}
        <div>
          <h2 className="ws-ccs-title">
            {session.offer_name || "Custom Creative Session"}
          </h2>
          <div className="ws-ccs-stages" data-testid="ws-ccs-stages">
            {STAGES.map((st, i) => (
              <React.Fragment key={st}>
                {i > 0 ? <span className="dim">→</span> : null}
                <span className={st === stage ? "ws-tag is-active" : "ws-tag dim"}>
                  {STAGE_LABEL[st]}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Two columns. The chat is mounted ONCE and keyed only by session id, so
          a DEVELOPMENT ↔ REVIEW switch never unmounts it and loses the
          conversation — the same reason v1's container does it here. */}
      <div className="ws-ccs-columns">
        <div className="ws-ccs-stage">{left}</div>
        <div className="ws-ccs-chat">
          <WorkspaceChat
            key={session.session_id}
            sessionId={session.session_id}
            api={api}
            copyTargets={copyTargets}
            onCopyToField={handleCopyToField}
            readOnly={stage === "SUBMITTED"}
          />
        </div>
      </div>
    </div>
  );
}
